// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { CodeableConcept, Extension, Patient } from '@medplum/fhirtypes';

/**
 * Where intake stores the "about you" answers on Patient that FHIR has no core
 * element for, and how provider-app reads them back.
 */

/** Repeats once per method, each with a `valueCoding` from {@link CONTACT_METHODS}. */
export const PREFERRED_CONTACT_METHOD_EXTENSION_URL =
  'http://polarissleep.com/fhir/StructureDefinition/preferred-communication-method';

/** Codes match the `preferred-communication-method` item of the self-referral intake Questionnaire. */
export const CONTACT_METHODS = [
  { code: 'email', display: 'Email' },
  { code: 'sms', display: 'Text Message' },
  { code: 'phone', display: 'Phone Call' },
] as const;

export type ContactMethodCode = (typeof CONTACT_METHODS)[number]['code'];

/** Repeats once per concern, each with a `valueCodeableConcept` from {@link SLEEP_CONCERN_SYSTEM}. */
export const SLEEP_CONCERN_EXTENSION_URL = 'https://polarissleep.com/fhir/StructureDefinition/patient-sleep-concern';
export const SLEEP_CONCERN_SYSTEM = 'https://polarissleep.com/fhir/CodeSystem/sleep-concern';

export const SLEEP_CONCERN_SOMETHING_ELSE = 'something-else';
export const SLEEP_CONCERN_NOT_SURE = 'not-sure';

/** In the order intake lists them. */
export const SLEEP_CONCERNS = [
  { code: 'snoring', display: 'Snoring' },
  { code: 'breathing-pauses', display: 'Breathing pauses, gasping, or choking during sleep' },
  { code: 'daytime-sleepiness', display: 'Feeling sleepy during the day' },
  { code: 'unrefreshed', display: 'Waking up tired or unrefreshed' },
  { code: 'insomnia', display: 'Trouble falling asleep or staying asleep' },
  { code: 'diagnosed-apnea', display: 'Help with diagnosed sleep apnea' },
  { code: 'cpap-trouble', display: 'Trouble using CPAP or questions about treatment options' },
  { code: 'review-treatment', display: 'Reviewing or updating my current sleep treatment' },
  { code: 'transfer-care', display: 'Transferring my sleep care to Polaris' },
  { code: 'provider-recommended', display: 'My healthcare provider recommended a sleep evaluation' },
  { code: SLEEP_CONCERN_SOMETHING_ELSE, display: 'Something else' },
  { code: SLEEP_CONCERN_NOT_SURE, display: "I'm not sure" },
] as const;

export type SleepConcernCode = (typeof SLEEP_CONCERNS)[number]['code'];

export interface SleepConcern {
  code: SleepConcernCode;
  /** The patient's own words; kept only for "Something else". */
  description?: string;
}

/** USPS code of the state the patient said they will be in for their visit, as a `valueCode`. */
export const VISIT_STATE_EXTENSION_URL = 'https://polarissleep.com/fhir/StructureDefinition/patient-visit-state';

/** The name the patient asked to be called, stored as the `usual` HumanName. */
export function getPreferredName(patient: Patient): string | undefined {
  return patient.name?.find((name) => name.use === 'usual')?.given?.[0];
}

/** Replaces the patient's preferred (`usual`) name; a blank name removes it. Kept after the legal name. */
export function withPreferredName(patient: Patient, preferredName: string | undefined): Patient {
  const names = (patient.name ?? []).filter((name) => name.use !== 'usual');
  const trimmed = preferredName?.trim();
  const name = [...names, ...(trimmed ? [{ use: 'usual' as const, given: [trimmed] }] : [])];
  return { ...patient, name: name.length ? name : undefined };
}

export function getVisitState(patient: Patient): string | undefined {
  return patient.extension?.find((extension) => extension.url === VISIT_STATE_EXTENSION_URL)?.valueCode;
}

/** Replaces the patient's visit state. */
export function withVisitState(patient: Patient, state: string): Patient {
  const extension: Extension[] = [
    ...(patient.extension ?? []).filter((entry) => entry.url !== VISIT_STATE_EXTENSION_URL),
    { url: VISIT_STATE_EXTENSION_URL, valueCode: state },
  ];
  return { ...patient, extension };
}

/** Display labels of the patient's preferred contact methods, in the order stored. */
export function getPreferredContactMethods(patient: Patient): string[] {
  return (patient.extension ?? [])
    .filter((extension) => extension.url === PREFERRED_CONTACT_METHOD_EXTENSION_URL)
    .map((extension) => {
      const coding = extension.valueCoding;
      return CONTACT_METHODS.find((method) => method.code === coding?.code)?.display ?? coding?.display ?? '';
    })
    .filter(Boolean);
}

/** The preferred `communication` language, falling back to the first one listed. */
export function getPrimaryLanguage(patient: Patient): string | undefined {
  const communication = patient.communication?.find((entry) => entry.preferred) ?? patient.communication?.[0];
  const language = communication?.language;
  return language?.text ?? language?.coding?.[0]?.display ?? language?.coding?.[0]?.code;
}

function sleepConcernConcept({ code, description }: SleepConcern): CodeableConcept {
  const display = SLEEP_CONCERNS.find((concern) => concern.code === code)?.display ?? code;
  const text = code === SLEEP_CONCERN_SOMETHING_ELSE ? description?.trim() : undefined;
  return { coding: [{ system: SLEEP_CONCERN_SYSTEM, code, display }], ...(text ? { text } : {}) };
}

/** Replaces the patient's sleep concerns. */
export function withSleepConcerns(patient: Patient, concerns: readonly SleepConcern[]): Patient {
  const extension: Extension[] = [
    ...(patient.extension ?? []).filter((entry) => entry.url !== SLEEP_CONCERN_EXTENSION_URL),
    ...concerns.map((concern) => ({
      url: SLEEP_CONCERN_EXTENSION_URL,
      valueCodeableConcept: sleepConcernConcept(concern),
    })),
  ];
  return { ...patient, extension };
}

/** Display labels of the patient's sleep concerns; "Something else" carries the patient's description. */
export function getSleepConcerns(patient: Patient): string[] {
  return (patient.extension ?? [])
    .filter((extension) => extension.url === SLEEP_CONCERN_EXTENSION_URL)
    .map((extension) => {
      const concept = extension.valueCodeableConcept;
      const coding = concept?.coding?.[0];
      const display = SLEEP_CONCERNS.find((concern) => concern.code === coding?.code)?.display ?? coding?.display;
      if (coding?.code === SLEEP_CONCERN_SOMETHING_ELSE && concept?.text) {
        return `${display}: ${concept.text}`;
      }
      return display ?? concept?.text ?? '';
    })
    .filter(Boolean);
}
